import { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Snackbar,
  InputAdornment,
  Checkbox,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  Build as BuildIcon,
  Info as InfoIcon,
  LinkSharp as LinkIcon,
  FileUpload as ExportIcon,
} from '@mui/icons-material'
import { RootState } from '../store'
import { UserInterface, isServerErrorResponse } from '../adapters/ApiSchema'
import { dcpsConfig } from '../util/Services'
import ApiRequestHandler from '../util/APIRequestHandler'
import defaultStore from '../util/Globale'
import { getFromLS } from '../util/storage'
import { contentSearchFeatureFlagKey } from '../components/appBar/ContentSearch'
import { doAuthDiagFeatureFlagKey } from '../components/appBar/AppNavBar'
import { useDynamicNavFeatureFlagKey } from '../util/Globale'
import { getSession } from '../lib/impersonation'

interface NavItem {
  Id: string
  Path: string
  Parent: string
  Type: 'Link' | 'Folder'
  Name: string
  DisplayName?: string
  Description: string
  Icon: string
  Weight: number
  Enabled: boolean
  Pinned: boolean
  Url?: string
  HasUrlKey: boolean
  Acls: { Name: string; Parameters: string[] }[]
  Managers: string[]
}

interface NavFormData {
  Name: string
  Type: 'Link' | 'Folder'
  Url: string
  Icon: string
  Description: string
  Weight: number
  Enabled: boolean
  Pinned: boolean
  Acls: { Name: string; Parameters: string[] }[]
}

const FOLDERS = ['My Links', 'Blended Learning', 'How To', 'District Links']

const ACL_OPTIONS = [
  'Allow-Any',
  'Allow-AllStaff',
  'Allow-Student',
  'Allow-Teacher',
  'Allow-Principal',
  'Allow-Assistant-Principal',
  'Allow-Parent',
  'Allow-Public',
  'Allow-By-Group',
  'Allow-By-User',
  'Allow-By-Grade-Level',
  'Allow-DAT-User',
  'Allow-School-Admin',
  'Allow-Outreach-User',
  'Allow-PGA-AnyRole',
]

const emptyForm: NavFormData = {
  Name: '',
  Type: 'Link',
  Url: '',
  Icon: '',
  Description: '',
  Weight: 100,
  Enabled: true,
  Pinned: false,
  Acls: [{ Name: 'Allow-AllStaff', Parameters: [] }],
}

const apiHandler = new ApiRequestHandler(dcpsConfig)

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index } = props
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null
}

function SystemInfoTab({ user }: { user: UserInterface | undefined }) {
  const impersonation = getSession()
  const featureFlags: Record<string, string> = {}
  ;[contentSearchFeatureFlagKey, doAuthDiagFeatureFlagKey, useDynamicNavFeatureFlagKey].forEach(key => {
    try {
      featureFlags[key] = JSON.stringify(getFromLS(key))
    } catch {
      featureFlags[key] = 'error'
    }
  })

  const sysRows = [
    { label: 'Environment', value: defaultStore.isProduction ? 'Production' : 'Development' },
    { label: 'API Host', value: dcpsConfig.hostname },
    { label: 'Build ID', value: defaultStore.buildId },
    { label: 'Build Number', value: defaultStore.buildNumber },
    { label: 'Pipeline', value: `${defaultStore.buildDef} v${defaultStore.buildDefVersion}` },
    { label: 'Source Version', value: defaultStore.buildSourceVersion || 'N/A' },
    { label: 'Dynamic Nav', value: defaultStore.useDynamicNav ? 'Enabled' : 'Disabled' },
    { label: 'Map System', value: defaultStore.mapSystem },
    { label: 'User Agent', value: navigator.userAgent },
    { label: 'UPN Override', value: getFromLS<string>('upn') || 'None' },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {impersonation && (
        <Alert severity="warning">
          Currently impersonating: <strong>{impersonation.targetName}</strong> ({impersonation.targetUpn})
        </Alert>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            User Info
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {user ? (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {[
                    ['Display Name', user.DisplayName],
                    ['Email / UPN', user.Email],
                    ['Object ID', user.ObjectID],
                    ['Is Imperator', user.IsImperator ? 'Yes' : 'No'],
                    ['Can Edit Profile', user.CanEditProfile ? 'Yes' : 'No'],
                    ['Has Student Info', user.HasStudentInfo ? 'Yes' : 'No'],
                    ['Has Student Devices', user.HasStudentDevices ? 'Yes' : 'No'],
                  ].map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell sx={{ fontWeight: 600, width: 200, border: 'none', py: 0.5 }}>{label}</TableCell>
                      <TableCell sx={{ border: 'none', py: 0.5, fontFamily: 'monospace' }}>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No user loaded</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            System &amp; Build
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {sysRows.map(row => (
                  <TableRow key={row.label}>
                    <TableCell sx={{ fontWeight: 600, width: 200, border: 'none', py: 0.5 }}>{row.label}</TableCell>
                    <TableCell sx={{ border: 'none', py: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Feature Flags
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {Object.entries(featureFlags).map(([key, val]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 600, width: 200, border: 'none', py: 0.5, fontFamily: 'monospace' }}>
                      {key}
                    </TableCell>
                    <TableCell sx={{ border: 'none', py: 0.5, fontFamily: 'monospace' }}>{val}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Full User Object (JSON)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 400,
              fontSize: '0.8rem',
              fontFamily: 'monospace',
            }}
          >
            {user ? JSON.stringify(user, null, 2) : 'No user'}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

function NavItemDialog({
  open,
  onClose,
  onSave,
  initialData,
  folder,
  mode,
}: {
  open: boolean
  onClose: () => void
  onSave: (data: NavFormData) => void
  initialData: NavFormData
  folder: string
  mode: 'add' | 'edit'
}) {
  const [form, setForm] = useState<NavFormData>(initialData)
  const [newAclName, setNewAclName] = useState('')

  useEffect(() => {
    setForm(initialData)
  }, [initialData])

  const updateField = (field: keyof NavFormData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const addAcl = () => {
    if (!newAclName) return
    setForm(prev => ({
      ...prev,
      Acls: [...prev.Acls, { Name: newAclName, Parameters: [] }],
    }))
    setNewAclName('')
  }

  const removeAcl = (index: number) => {
    setForm(prev => ({
      ...prev,
      Acls: prev.Acls.filter((_, i) => i !== index),
    }))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'add' ? `Add Link to ${folder}` : `Edit: ${initialData.Name}`}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField
          label="Name"
          value={form.Name}
          onChange={e => updateField('Name', e.target.value)}
          required
          fullWidth
          size="small"
        />
        <TextField
          label="URL"
          value={form.Url}
          onChange={e => updateField('Url', e.target.value)}
          fullWidth
          size="small"
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Icon (MUI name)"
            value={form.Icon}
            onChange={e => updateField('Icon', e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Weight (sort order)"
            type="number"
            value={form.Weight}
            onChange={e => updateField('Weight', parseInt(e.target.value) || 0)}
            size="small"
            sx={{ width: 140 }}
          />
        </Box>
        <TextField
          label="Description"
          value={form.Description}
          onChange={e => updateField('Description', e.target.value)}
          fullWidth
          size="small"
          multiline
          rows={2}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControlLabel
            control={<Switch checked={form.Enabled} onChange={e => updateField('Enabled', e.target.checked)} />}
            label="Enabled"
          />
          <FormControlLabel
            control={<Switch checked={form.Pinned} onChange={e => updateField('Pinned', e.target.checked)} />}
            label="Pinned"
          />
        </Box>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
          Access Control (ACLs)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {form.Acls.map((acl, i) => (
            <Chip
              key={`${acl.Name}-${i}`}
              label={acl.Parameters.length > 0 ? `${acl.Name} (${acl.Parameters.length})` : acl.Name}
              onDelete={() => removeAcl(i)}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Add ACL</InputLabel>
            <Select value={newAclName} label="Add ACL" onChange={e => setNewAclName(e.target.value as string)}>
              {ACL_OPTIONS.filter(o => !form.Acls.some(a => a.Name === o)).map(o => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={addAcl} disabled={!newAclName} size="small">
            Add
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} disabled={!form.Name}>
          {mode === 'add' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface V1Link {
  NavType: string
  Url?: string
  Script?: null
  Name: string
  Description: string
  Icon: string
  Acl: string
  Properties: Record<string, string>
}

interface V1Folder {
  NavType: 'Folder'
  Name: string
  Description: string
  Icon: string
  Acl: string
  Items: V1Link[]
}

interface ProdV1Item {
  NavType: string
  Name: string
  Acl?: string
  Properties?: Record<string, string>
  Items?: ProdV1Item[]
}

function buildGuidMapping(prodData: { Items?: ProdV1Item[] }, devItems: Map<string, NavItem[]>): Map<string, string> {
  const mapping = new Map<string, string>()
  if (!prodData?.Items) return mapping

  for (const prodFolder of prodData.Items) {
    if (prodFolder.NavType !== 'Folder' || !prodFolder.Items) continue
    const devFolderItems = devItems.get(prodFolder.Name) || []

    for (const prodItem of prodFolder.Items) {
      const devMatch = devFolderItems.find(d => d.Name === prodItem.Name)
      if (!devMatch) continue

      const prodGroups = (prodItem.Properties?.AllowedGroups || '')
        .split(';')
        .map(s => s.trim())
        .filter(Boolean)
      const prodUsers = (prodItem.Properties?.AllowedUsers || '')
        .split(';')
        .map(s => s.trim())
        .filter(Boolean)

      const devGroupAcl = devMatch.Acls.find(a => a.Name === 'Allow-By-Group')
      const devUserAcl = devMatch.Acls.find(a => a.Name === 'Allow-By-User')

      if (devGroupAcl && prodGroups.length === devGroupAcl.Parameters.length) {
        devGroupAcl.Parameters.forEach((guid, i) => {
          if (prodGroups[i] && !mapping.has(guid)) mapping.set(guid, prodGroups[i])
        })
      }
      if (devUserAcl && prodUsers.length === devUserAcl.Parameters.length) {
        devUserAcl.Parameters.forEach((guid, i) => {
          if (prodUsers[i] && !mapping.has(guid)) mapping.set(guid, prodUsers[i])
        })
      }
    }

    // Also check nested subfolders in Prod
    for (const prodItem of prodFolder.Items) {
      if (prodItem.NavType === 'Folder' && prodItem.Items) {
        for (const subItem of prodItem.Items) {
          const devMatch = devFolderItems.find(d => d.Name === subItem.Name)
          if (!devMatch) continue
          const subGroups = (subItem.Properties?.AllowedGroups || '')
            .split(';')
            .map(s => s.trim())
            .filter(Boolean)
          const subUsers = (subItem.Properties?.AllowedUsers || '')
            .split(';')
            .map(s => s.trim())
            .filter(Boolean)
          const devGroupAcl = devMatch.Acls.find(a => a.Name === 'Allow-By-Group')
          const devUserAcl = devMatch.Acls.find(a => a.Name === 'Allow-By-User')
          if (devGroupAcl && subGroups.length === devGroupAcl.Parameters.length) {
            devGroupAcl.Parameters.forEach((guid, i) => {
              if (subGroups[i] && !mapping.has(guid)) mapping.set(guid, subGroups[i])
            })
          }
          if (devUserAcl && subUsers.length === devUserAcl.Parameters.length) {
            devUserAcl.Parameters.forEach((guid, i) => {
              if (subUsers[i] && !mapping.has(guid)) mapping.set(guid, subUsers[i])
            })
          }
        }
      }
    }
  }
  return mapping
}

function convertToV1(items: NavItem[], guidMap: Map<string, string>): V1Link[] {
  return items
    .filter(item => item.Type === 'Link')
    .map(item => {
      const simpleAcls: string[] = []
      const groupNames: string[] = []
      const userNames: string[] = []

      for (const acl of item.Acls) {
        if (acl.Name === 'Allow-By-Group') {
          simpleAcls.push('Allow-By-Group')
          for (const guid of acl.Parameters) {
            groupNames.push(guidMap.get(guid) || `[GUID:${guid}]`)
          }
        } else if (acl.Name === 'Allow-By-User') {
          simpleAcls.push('Allow-By-User')
          for (const guid of acl.Parameters) {
            userNames.push(guidMap.get(guid) || `[GUID:${guid}]`)
          }
        } else {
          simpleAcls.push(acl.Name)
        }
      }

      const aclStr = simpleAcls.length > 0 ? simpleAcls.join(';') : ''
      const properties: Record<string, string> = {}
      if (groupNames.length > 0) properties.AllowedGroups = groupNames.join('; ')
      if (userNames.length > 0) properties.AllowedUsers = userNames.join('; ')

      const v1Item: V1Link = {
        NavType: 'Link',
        Url: item.Url || '',
        Script: null,
        Name: item.Name,
        Description: item.Description,
        Icon: item.Icon,
        Acl: aclStr,
        Properties: properties,
      }
      return v1Item
    })
}

function ExportDialog({
  open,
  onClose,
  allDevItems,
  snackbar,
}: {
  open: boolean
  onClose: () => void
  allDevItems: Map<string, NavItem[]>
  snackbar: (msg: string, sev: 'success' | 'error') => void
}) {
  const [prodJson, setProdJson] = useState('')
  const [output, setOutput] = useState('')
  const [unresolvedCount, setUnresolvedCount] = useState(0)
  const [totalGuids, setTotalGuids] = useState(0)

  const FOLDER_ICONS: Record<string, string> = {
    'My Links': 'Link',
    'Blended Learning': 'LocalLibrary',
    'How To': 'HelpOutline',
    'District Links': 'Language',
  }

  const generate = () => {
    let guidMap = new Map<string, string>()
    if (prodJson.trim()) {
      try {
        const parsed = JSON.parse(prodJson)
        const prodData = parsed.Items ? parsed : { Items: parsed }
        guidMap = buildGuidMapping(prodData, allDevItems)
      } catch {
        snackbar('Invalid JSON in Prod Reference field', 'error')
        return
      }
    }

    const allGuids = new Set<string>()
    allDevItems.forEach(items => {
      items.forEach(item => {
        item.Acls.forEach(acl => {
          if (acl.Name === 'Allow-By-Group' || acl.Name === 'Allow-By-User') {
            acl.Parameters.forEach(g => allGuids.add(g))
          }
        })
      })
    })

    const unresolved = [...allGuids].filter(g => !guidMap.has(g))
    setTotalGuids(allGuids.size)
    setUnresolvedCount(unresolved.length)

    const v1Folders: V1Folder[] = FOLDERS.map(folderName => ({
      NavType: 'Folder' as const,
      Name: folderName,
      Description: '',
      Icon: FOLDER_ICONS[folderName] || '',
      Acl: 'Allow-Any',
      Items: convertToV1(allDevItems.get(folderName) || [], guidMap),
    }))

    const v1Root = { Items: v1Folders }
    setOutput(JSON.stringify(v1Root, null, 2))
  }

  const copyOutput = () => {
    navigator.clipboard.writeText(output)
    snackbar('Copied to clipboard', 'success')
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Export for Prod (V1 JSON)</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <Alert severity="info" variant="outlined">
          Exporting <strong>{[...allDevItems.values()].reduce((n, arr) => n + arr.length, 0)} selected link(s)</strong>{' '}
          from <strong>{[...allDevItems.keys()].join(', ')}</strong>. To auto-resolve GUIDs to display names, paste the
          current Prod <code>MainActionMenu.json</code> config below before generating.
        </Alert>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              Prod Reference JSON (optional &mdash; paste to auto-resolve GUIDs)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              multiline
              rows={6}
              fullWidth
              size="small"
              placeholder='Paste the Prod MainActionMenu currentconfig JSON here... (the full {"Items": [...]} object)'
              value={prodJson}
              onChange={e => setProdJson(e.target.value)}
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
            />
          </AccordionDetails>
        </Accordion>

        <Button variant="contained" onClick={generate} startIcon={<ExportIcon />}>
          Generate V1 JSON
        </Button>

        {output && (
          <>
            {totalGuids > 0 && (
              <Alert severity={unresolvedCount > 0 ? 'warning' : 'success'}>
                {unresolvedCount === 0
                  ? `All ${totalGuids} GUIDs resolved successfully.`
                  : `${totalGuids - unresolvedCount} of ${totalGuids} GUIDs resolved. ${unresolvedCount} unresolved (marked with [GUID:...]).`}
              </Alert>
            )}

            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Copy to clipboard">
                <IconButton
                  size="small"
                  onClick={copyOutput}
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'background.paper' }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 400,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {output}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function NavigationTab() {
  const [selectedFolder, setSelectedFolder] = useState(FOLDERS[0])
  const [items, setItems] = useState<NavItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchFilter, setSearchFilter] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add')
  const [dialogData, setDialogData] = useState<NavFormData>(emptyForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: NavItem | null }>({
    open: false,
    item: null,
  })
  const [exportOpen, setExportOpen] = useState(false)
  const [allDevItems, setAllDevItems] = useState<Map<string, NavItem[]>>(new Map())
  const [exportLoading, setExportLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const baseUrl = dcpsConfig.hostname

  const fetchItems = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const url = `${baseUrl}/api/navigation/root/${encodeURIComponent(selectedFolder)}`
      const result = await apiHandler.getOrDelete<NavItem[]>(url, {})
      if (isServerErrorResponse(result)) {
        setError(result.Message || 'Failed to fetch items')
        setItems([])
      } else {
        setItems(result)
      }
    } catch (ex: any) {
      setError(ex?.message || 'Network error')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [selectedFolder, baseUrl])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const handleAdd = () => {
    setDialogMode('add')
    setDialogData(emptyForm)
    setEditingId(null)
    setDialogOpen(true)
  }

  const handleEdit = (item: NavItem) => {
    setDialogMode('edit')
    setDialogData({
      Name: item.Name,
      Type: item.Type,
      Url: item.Url || '',
      Icon: item.Icon,
      Description: item.Description,
      Weight: item.Weight,
      Enabled: item.Enabled,
      Pinned: item.Pinned,
      Acls: item.Acls,
    })
    setEditingId(item.Id)
    setDialogOpen(true)
  }

  const handleSave = async (data: NavFormData) => {
    try {
      const folderPath = encodeURIComponent(selectedFolder)
      let result: any

      if (dialogMode === 'add') {
        const url = `${baseUrl}/api/navigation/root/${folderPath}`
        result = await apiHandler.updateMethod(url, { method: 'PUT', data })
      } else {
        const url = `${baseUrl}/api/navigation/root/${folderPath}`
        result = await apiHandler.updateMethod(url, { method: 'PATCH', data: { ...data, Id: editingId } })
      }

      if (isServerErrorResponse(result)) {
        setSnackbar({ open: true, message: `Error: ${result.Message}`, severity: 'error' })
      } else {
        setSnackbar({
          open: true,
          message: `${dialogMode === 'add' ? 'Created' : 'Updated'} "${data.Name}" successfully`,
          severity: 'success',
        })
        setDialogOpen(false)
        fetchItems()
      }
    } catch (ex: any) {
      setSnackbar({ open: true, message: ex?.message || 'Error saving', severity: 'error' })
    }
  }

  const handleDelete = async () => {
    const item = deleteConfirm.item
    if (!item) return
    try {
      const url = `${baseUrl}/api/navigation/root/${encodeURIComponent(selectedFolder)}/${item.Id}`
      const result = await apiHandler.getOrDelete(url, { method: 'DELETE' })
      if (isServerErrorResponse(result)) {
        setSnackbar({ open: true, message: `Error: ${result.Message}`, severity: 'error' })
      } else {
        setSnackbar({ open: true, message: `Deleted "${item.Name}"`, severity: 'success' })
        fetchItems()
      }
    } catch (ex: any) {
      setSnackbar({ open: true, message: ex?.message || 'Error deleting', severity: 'error' })
    }
    setDeleteConfirm({ open: false, item: null })
  }

  const handleFlushCache = async () => {
    try {
      const url = `${baseUrl}/api/navigation/mainActionMenu?flush=true`
      await apiHandler.getOrDelete(url, {})
      setSnackbar({ open: true, message: 'Cache flushed successfully', severity: 'success' })
    } catch {
      setSnackbar({ open: true, message: 'Failed to flush cache', severity: 'error' })
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filtered.map(i => i.Id)))
    }
  }

  const handleExport = async () => {
    if (selectedIds.size === 0) {
      setSnackbar({ open: true, message: 'Select at least one link to export', severity: 'error' })
      return
    }
    setExportLoading(true)
    try {
      const selectedItems = items.filter(i => selectedIds.has(i.Id))
      const exportMap = new Map<string, NavItem[]>()
      exportMap.set(selectedFolder, selectedItems)
      setAllDevItems(exportMap)
      setExportOpen(true)
    } finally {
      setExportLoading(false)
    }
  }

  const filtered = items.filter(
    item =>
      item.Name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      (item.Url || '').toLowerCase().includes(searchFilter.toLowerCase())
  )

  const enabledCount = items.filter(i => i.Enabled).length
  const disabledCount = items.filter(i => !i.Enabled).length

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Folder</InputLabel>
          <Select
            value={selectedFolder}
            label="Folder"
            onChange={e => {
              setSelectedFolder(e.target.value)
              setSelectedIds(new Set())
            }}
          >
            {FOLDERS.map(f => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Filter links..."
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Typography variant="body2" color="text.secondary">
          {items.length} total ({enabledCount} enabled, {disabledCount} disabled)
        </Typography>

        <Tooltip title="Export selected links as V1 JSON for Prod">
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={handleExport}
            startIcon={exportLoading ? <CircularProgress size={16} /> : <ExportIcon />}
            disabled={exportLoading || selectedIds.size === 0}
          >
            Export Selected ({selectedIds.size})
          </Button>
        </Tooltip>

        <Tooltip title="Flush navigation cache">
          <Button size="small" variant="outlined" onClick={handleFlushCache} startIcon={<RefreshIcon />}>
            Flush Cache
          </Button>
        </Tooltip>

        <Tooltip title="Refresh list">
          <IconButton onClick={fetchItems} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Link
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 42 }}>
                  <Checkbox
                    size="small"
                    indeterminate={selectedIds.size > 0 && selectedIds.size < filtered.length}
                    checked={filtered.length > 0 && selectedIds.size === filtered.length}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 100 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 80 }}>Weight</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ACLs</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {searchFilter ? 'No matching items' : 'No items in this folder'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((item, idx) => (
                  <TableRow
                    key={item.Id}
                    hover
                    selected={selectedIds.has(item.Id)}
                    sx={{ '&:last-child td': { border: 0 }, cursor: 'pointer' }}
                    onClick={() => toggleSelect(item.Id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selectedIds.has(item.Id)} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {item.DisplayName || item.Name}
                          {item.Pinned && (
                            <Chip label="Pinned" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Typography>
                        {item.Url && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              maxWidth: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {item.Url}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.Enabled ? 'Active' : 'Disabled'}
                        size="small"
                        color={item.Enabled ? 'success' : 'default'}
                        variant={item.Enabled ? 'filled' : 'outlined'}
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {item.Weight}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                        {item.Acls.slice(0, 3).map((acl, i) => (
                          <Chip
                            key={`${acl.Name}-${i}`}
                            label={acl.Name.replace('Allow-', '')}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        ))}
                        {item.Acls.length > 3 && (
                          <Chip
                            label={`+${item.Acls.length - 3}`}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0, justifyContent: 'flex-end' }}>
                        {item.Url && (
                          <Tooltip title="Open URL">
                            <IconButton size="small" onClick={() => window.open(item.Url, '_blank')}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Copy ID">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(item.Id)
                              setSnackbar({ open: true, message: `Copied ID: ${item.Id}`, severity: 'success' })
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ open: true, item })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <NavItemDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initialData={dialogData}
        folder={selectedFolder}
        mode={dialogMode}
      />

      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, item: null })}>
        <DialogTitle>Delete Link</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm.item?.Name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        allDevItems={allDevItems}
        snackbar={(msg, sev) => setSnackbar({ open: true, message: msg, severity: sev })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export function DiagPage(props: { user: UserInterface | undefined }) {
  const [tabIndex, setTabIndex] = useState(0)
  const userState = useSelector((state: RootState) => state.user.user)
  const user = props.user || userState

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <BuildIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Admin Tools
        </Typography>
        <Chip
          label={defaultStore.isProduction ? 'PROD' : 'DEV'}
          size="small"
          color={defaultStore.isProduction ? 'error' : 'info'}
          sx={{ ml: 1 }}
        />
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: 'grey.50' }}
        >
          <Tab icon={<InfoIcon />} iconPosition="start" label="System Info" sx={{ textTransform: 'none' }} />
          <Tab icon={<LinkIcon />} iconPosition="start" label="Navigation Manager" sx={{ textTransform: 'none' }} />
        </Tabs>

        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <TabPanel value={tabIndex} index={0}>
            <SystemInfoTab user={user} />
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <NavigationTab />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  )
}
